import { useCallback, useEffect, useState } from 'react';
export interface AndroidBridge {
  /** function for requesting the location from the app */
  getLocationId: () => string | null;

  /** Callback function for sending the auth token from the website to the app */
  onToken: (token: string | null) => void;

  /** Request reset of hubs at provided location (exact location only) */
  resetDiscovery: () => void;
}

declare global {
  interface Window {
    __PERIVERSE_ANDROID_BRIDGE__?: AndroidBridge;
  }
}

/**
 * Return type for useRealtimeCaseUpdates hook
 */
export interface UseAndroidResult {
  /** Whether the website is being loading on the tablet */
  isAndroid: boolean;
  /** The location id (if available) that is set on the tablet */
  locationId: string | null;

  resetDiscovery: () => void;
}

/**
 * React hook for interacting with the peritas android app.
 * For use when the website is loaded inside the embedded browser of the device
 * For example the tablets located at the checkpoint
 *
 * @param _options - Configuration options for the real-time connection
 * @returns Connection state and error information
 */
export function useAndroid(): UseAndroidResult {
  const [isAndroid, setIsAndroid] = useState(false);
  const [locationId, setLocationId] = useState<string | null>(null);
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      typeof window.__PERIVERSE_ANDROID_BRIDGE__ !== 'undefined'
    ) {
      setIsAndroid(true);
      const locationId = window.__PERIVERSE_ANDROID_BRIDGE__.getLocationId();
      setLocationId(locationId);
    } else {
      setIsAndroid(false);
      setLocationId(null);
    }
  }, []);

  const resetDiscovery = useCallback(
    () => {
      if (
        typeof window !== 'undefined' &&
        typeof window.__PERIVERSE_ANDROID_BRIDGE__ !== 'undefined'
      ) {
        window.__PERIVERSE_ANDROID_BRIDGE__.resetDiscovery();
      }
    },
    []
  );

  return {
    isAndroid,
    locationId,
    resetDiscovery,
  };
}
